<template>
  <div class="relative" ref="selectContainer">
    <!--  SELECTED OPTION  -->
    <div
      class="min-w-[100px] transition-200 px-3 h-10 py-[9px] transition-all duration-300 border-2 border-gray-250 flex items-center justify-between rounded-xl"
      tabindex="1"
      ref="selectTrigger"
      :class="[
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        selectedOptionStyles,
        error ? '!border-red bg-red-100' : '',
        { 'focus-within:border-gray-100': disabled },
        headStyles,
        { 'border-green': showOptions },
      ]"
      @click="!disabled ? toggleSelect(!showOptions) : ''"
    >
      <span :class="[prefixClass]">
        <slot name="prefix" />
      </span>
      <slot name="selectedOption" :value="value">
        <div class="w-full flex-y-center justify-between">
          <div class="flex items-baseline gap-2">
            <img
              v-if="value?.icon"
              :src="value?.icon"
              alt=""
              class="h-3 w-auto"
            />
            <p
              tabindex="1"
              v-if="!value"
              class="!text-gray font-semibold select-none text-sm leading-140"
              :class="{ '!text-red': error, '!text-gray': disabled }"
            >
              {{ placeholder ?? $t("select") }}
            </p>
            <p
              v-else
              class="select-none font-semibold text-sm text-dark leading-130 capitalize line-clamp-1"
              tabindex="1"
              :class="[{ '!text-gray': disabled }, selectedStyles]"
            >
              {{ value[labelKey] || value }}
            </p>
          </div>

          <slot name="chevron">
            <span
              class="icon-chevron-right rotate-90 flex-center h-4 transition-200 text-xs text-gray-450 inline-block shrink-0 ml-4"
              :class="{ '!-rotate-90': showOptions }"
            >
            </span>
          </slot>
        </div>
      </slot>
    </div>
    <!--  OPTIONS  -->
    <Transition name="fade">
      <ul
        v-if="showOptions && !disabled"
        :key="showOptions"
        :class="{ top: isTopOpen }"
        ref="dropdown"
        class="select-dropdown absolute w-full bg-white-100 border-2 border-gray-250 z-[3] translate-y-1 overflow-hidden max-h-[250px] overflow-y-auto text-white rounded-xl shadow-select"
      >
        <slot name="options">
          <slot name="search">
            <li></li>
          </slot>
          <template v-if="options?.length">
            <li
              v-for="(option, idx) in options"
              :key="idx"
              class="transition-300"
              :class="{
                'cursor-pointer hover:bg-gray-150': !isDisabled(option),
                'cursor-not-allowed opacity-50': isDisabled(option),
                'bg-gray-250': isActive(option),
              }"
              @click="!isDisabled(option) && onSelect(option)"
            >
              <slot
                name="option"
                :option="option"
                :index="idx"
                :disabled="isDisabled(option)"
              >
                <p class="flex-y-center justify-between py-2 px-[14px]">
                  <span class="flex-y-center gap-2">
                    <span v-if="option.icon">
                      <img :src="option.icon" alt="" class="h-3 w-auto" />
                    </span>

                    <span
                      class="text-gray-450 text-sm font-semibold capitalize"
                      :class="{ '!text-dark-300': isActive(option) }"
                    >
                      {{ option[labelKey] }}
                    </span>
                  </span>

                  <span
                    v-if="isDisabled(option)"
                    class="text-green text-sm font-semibold capitalize"
                  >
                    {{ $t("coming_soon") }}
                  </span>
                  <i
                    v-if="isActive(option) && activeIcon"
                    class="icon-tick-square text-sm text-green"
                  ></i>
                </p>
              </slot>
            </li>
            <div class="flex-y-center flex-col gap-2" v-if="loading">
              <CPreloader
                v-for="i in 12"
                :key="i"
                class="w-full"
                width="100%"
                height="36px"
                :loading="loading"
              />
            </div>
          </template>
          <template v-if="!options?.length">
            <div
              class="transition-300 cursor-pointer rounded hover:bg-green-100"
            >
              <p
                class="flex-y-center justify-between py-2 px-3 text-dark text-[13px]"
              >
                {{ $t("no_results") }}
              </p>
            </div>
          </template>
          <div v-if="infiniteScroll" ref="target" class="py-0.5 w-full"></div>
        </slot>
      </ul>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useIntersectionObserver } from "@vueuse/core";
import { nextTick, ref, watch } from "vue";
import { TClassName } from "@/types/common";
import CPreloader from "@/components/CPreloader.vue";

type TOption = string | number | { [key: string]: string | number };

export interface Props {
  modelValue?: TOption;
  options: TOption[];
  labelKey: string;
  valueKey: string;
  placeholder?: string;
  selectedOptionStyles?: TClassName;
  selectedStyles?: TClassName;
  prefixClass?: TClassName;
  headStyles?: TClassName;
  dark?: boolean;
  error?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  activeIcon?: boolean;
  infiniteScroll?: boolean;
  loading?: boolean;
  disabledOptions?: TOption[]; // New prop for disabled options
}

const props = withDefaults(defineProps<Props>(), {
  labelKey: "name",
  valueKey: "id",
  disabledOptions: () => [],
});

const emit = defineEmits<{
  (e: "on-toggle", value: boolean): void;
  (e: "update:modelValue", value: boolean): void;
  (e: "infinite-scroll"): void;
  (e: "load"): void;
  (e: "on-select", value: TOption): void;
}>();

const showOptions = ref(false);
const isTopOpen = ref(false);

const selectContainer = ref<HTMLElement | null>(null);
const selectTrigger = ref<HTMLElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);

const target = ref(null);
const targetIsVisible = ref(false);

function toggleSelect(newValue = showOptions.value) {
  showOptions.value = newValue;
  if (showOptions.value) {
    nextTick(() => {
      positionDropdown();
    });
  }
  emit("on-toggle", showOptions.value);
}

const positionDropdown = () => {
  if (!selectTrigger.value || !dropdown.value) return;

  const triggerRect = selectTrigger.value.getBoundingClientRect();
  const dropdownRect = dropdown.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  isTopOpen.value = spaceBelow < dropdownRect.height && spaceAbove > spaceBelow;
};

function findOption(option: TOption) {
  return props.options.find(
    (o) => o === option || o[props.valueKey] === option,
  );
}

const value = ref(findOption(props.modelValue));

function onSelect(option: TOption) {
  if (!isDisabled(option)) {
    value.value = option;
    toggleSelect(false);
    emit("update:modelValue", option[props.valueKey] || option);
    emit("on-select", option);
  }
}

const select = ref();
onClickOutside(select, () => toggleSelect(false));

function isActive(option: TOption) {
  return (
    option === value.value ||
    option[props.valueKey as keyof typeof option] === value.value ||
    (typeof value.value === "object" &&
      option[props.valueKey as keyof typeof option] ===
        value.value[props.valueKey])
  );
}

function isDisabled(option: TOption) {
  return props.disabledOptions.some(
    (disabledOption) =>
      disabledOption === option ||
      disabledOption[props.valueKey] === option[props.valueKey],
  );
}

useIntersectionObserver(target, ([{ isIntersecting }]) => {
  targetIsVisible.value = isIntersecting;
  if (isIntersecting) {
    emit("load");
  }
});

watch(
  () => targetIsVisible.value,
  (newValue) => {
    if (newValue) {
      emit("infinite-scroll");
    }
  },
);

watch(
  () => props.modelValue,
  (newValue) => {
    value.value = findOption(newValue);
  },
  { immediate: true },
);

// Recalculate position when options change
watch(
  () => props.options,
  () => {
    if (showOptions.value) {
      nextTick(() => {
        positionDropdown();
      });
    }
  },
);
</script>

<style scoped>
.select-dropdown.top {
  bottom: 100%;
  margin-bottom: 4px;
}

.select-dropdown:not(.top) {
  top: 100%;
  margin-top: 4px;
}
</style>
